import { handleCustomUDL } from "@utils/UDLUtils";

/**
 * V2Searchfield — ES6 replacement for the Terrific/Knockout searchfield module.
 *
 * Binds to server-rendered `.comp-v2-searchfield` HTML. On Enter key or
 * search-icon click, navigates to the target URL with the query appended
 * via URLSearchParams (preserving any existing params).
 *
 * @ticket AB-8817
 */
export class V2Searchfield {
  private root: HTMLElement;
  private input: HTMLInputElement;
  private searchIcon: HTMLElement;
  private form: HTMLFormElement;
  private targetUrl: string;

  constructor(root: HTMLElement) {
    this.root = root;
    this.input = root.querySelector(
      ".js-v2-sitesearchfield-input",
    ) as HTMLInputElement;
    this.searchIcon = root.querySelector(".fa-search") as HTMLElement;
    this.form = root.querySelector<HTMLFormElement>(
      ".js-v2-searchfield-form",
    )!;
    this.targetUrl = root.dataset.targetUrl || "";

    this.addEventListeners();
  }

  private addEventListeners(): void {
    this.input.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.redirectToResults();
      }
    });

    this.searchIcon.addEventListener("click", () => {
      this.redirectToResults();
    });

    this.form.addEventListener("submit", (e: Event) => {
      e.preventDefault();
    });
  }

  private redirectToResults(): void {
    const query = this.input.value;
    this.pushData(query);

    // Extract pathname only — the server may resolve an absolute URL pointing
    // to a different domain (e.g. publish) via Sling resource mapping.
    // Using the pathname with window.location.origin keeps the redirect on the
    // current domain, which works correctly on both author and publish.
    let targetPath: string;
    try {
      targetPath = new URL(this.targetUrl).pathname;
    } catch {
      targetPath = this.targetUrl;
    }

    const url = new URL(targetPath + ".html", window.location.origin);
    url.searchParams.set("query", query);
    window.location.assign(url.toString());
  }

  private pushData(searchTerm: string): void {
    handleCustomUDL("search_results_pageview", {
      search_term: searchTerm || "empty_query",
    });
  }

  /**
   * Discover all `.comp-v2-searchfield` elements in the DOM and instantiate.
   * @returns Array of V2Searchfield instances.
   */
  static async initAll(): Promise<V2Searchfield[]> {
    const elements = document.querySelectorAll<HTMLElement>(
      '[data-component="v2-searchfield"]',
    );
    return Array.from(elements).map((el) => new V2Searchfield(el));
  }
}
